/* eslint-disable import/prefer-default-export */
import { IClassificationProps, ICmnInspectionProps } from 'interface/inspection/InspectionInfo';
import { IInspectionIpsativesProps } from 'interface/project/projectInfo';
import { useMemo } from 'react';
import useInspection from './useInspection';
import { inspectionLogger, getStepName } from 'utils/inspectionLogger';
import { useAppDispatch } from 'store';
import { postInspectionClickLogs } from 'store/inspection/inspectionSlice';
import { useProject } from './useProject';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const usePlayer = (
  info: IInspectionIpsativesProps[],
  result: ICmnInspectionProps[],
) => {
  const dispatch = useAppDispatch();
  const { urlProjectId, urlPartCode } = useProject();
  const {
    progressInfo,
    changeProgressInfo,
    changeDetailProgressInfo,
    detailProgressInfo,
    classification,
    inspectionDetail,
    // inspectionDetail2Info, // 제거됨 - 새로 만들 예정
    // inspectionDetail3Info2, // 제거됨 - 새로 만들 예정
    inspectionFirstClassification,
  } = useInspection();

  // 2단계 분류에서는 inspectionFirstClassification에서 정보를 가져옴
  const inspectionDetail2Info = useMemo(
    () => ({
      NNInfo: inspectionFirstClassification.NNTemp,
      NInfo: inspectionFirstClassification.NTemp,
      AInfo: inspectionFirstClassification.ATemp,
      AAInfo: inspectionFirstClassification.AATemp,
    }),
    [inspectionFirstClassification],
  );
  const inspectionDetail3Info2 = useMemo(() => ({}) as Record<string, IClassificationProps[]>, []);
  const ipsatives = useMemo(() => {
    return info || [];
  }, [info]);

  // 1단계 분류에서는 inspectionFirstClassification에서 result 생성
  const firstClassificationResult = useMemo(() => {
    if (classification === '1') {
      return [
        ...inspectionFirstClassification.NNTemp,
        ...inspectionFirstClassification.NTemp,
        ...inspectionFirstClassification.ATemp,
        ...inspectionFirstClassification.AATemp,
      ];
    }
    return result;
  }, [classification, inspectionFirstClassification, result]);

  const buttonInfo = useMemo(() => {
    // 1단계: firstClassificationResult도 NN→N→A→AA 순이라 progressInfo와 표시 순서가 다름. 현재 문항 id로 찾기.
    if (classification === '1') {
      const currentItem = info[progressInfo];
      const currentId = currentItem?.id;
      const found = currentId ? firstClassificationResult.find((r: ICmnInspectionProps) => r.id === currentId) : null;
      return (
        found || {
          sort: -1,
          id: currentId || '',
          value: '',
        }
      );
    }
    // 2·3단계: result는 NN→N→A→AA 순으로 정렬된 배열이라 result[detailProgressInfo]와 표시 순서가 다름.
    // 현재 표시 중인 문항(info[detailProgressInfo])의 id로 result에서 찾아야 이전/다음 이동 시 체크가 맞음.
    if (['2_1', '2_2', '2_3', '2_4'].includes(classification) || classification === '3') {
      const currentItem = info[detailProgressInfo];
      const currentId = currentItem?.id;
      const found = currentId ? result.find((r: ICmnInspectionProps) => r.id === currentId) : null;
      return (
        found || {
          sort: -1,
          id: currentId || '',
          value: '',
        }
      );
    }
    return (
      firstClassificationResult[progressInfo] || {
        sort: -1,
        id: '',
        value: '',
      }
    );
  }, [classification, result, firstClassificationResult, progressInfo, detailProgressInfo, info]);
  const handlePrev = async () => {
    // 이전 버튼 클릭 로그 (비동기)
    if (urlProjectId && urlPartCode && inspectionDetail?.id) {
      const stepKey = classification as '1' | '2_1' | '2_2' | '2_3' | '2_4' | '3';
      inspectionLogger.logClick({
        projectId: urlProjectId,
        partCode: urlPartCode,
        inspectionId: inspectionDetail.id,
        step: stepKey,
        stepName: getStepName(stepKey),
        actionType: 'prev_button',
      });

      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 이전 버튼 클릭 로그 ${pendingLogs.length}개 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 이전 버튼 클릭 로그 전송 실패:', error);
            });
        }
      });
    }

    if (classification === '1') {
      await changeProgressInfo(progressInfo - 1);
    } else if (['2_1', '2_2', '2_3', '2_4'].includes(classification) || ['3'].includes(classification)) {
      await changeDetailProgressInfo(detailProgressInfo - 1);
    }
  };

  const handleNext = async () => {
    // 다음 버튼 클릭 로그 (비동기)
    if (urlProjectId && urlPartCode && inspectionDetail?.id) {
      const stepKey = classification as '1' | '2_1' | '2_2' | '2_3' | '2_4' | '3';
      inspectionLogger.logClick({
        projectId: urlProjectId,
        partCode: urlPartCode,
        inspectionId: inspectionDetail.id,
        step: stepKey,
        stepName: getStepName(stepKey),
        actionType: 'next_button',
      });

      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 다음 버튼 클릭 로그 ${pendingLogs.length}개 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 다음 버튼 클릭 로그 전송 실패:', error);
            });
        }
      });
    }

    if (classification === '1') {
      await changeProgressInfo(progressInfo + 1);
    } else if (['2_1', '2_2', '2_3', '2_4'].includes(classification) || ['3'].includes(classification)) {
      await changeDetailProgressInfo(detailProgressInfo + 1);
    }
  };

  const isNextDisabled = useMemo(() => {
    if (classification === '1') {
      return (
        typeof firstClassificationResult[progressInfo]?.value === 'undefined'
        || !['NN', 'N', 'A', 'AA'].includes(firstClassificationResult[progressInfo]?.value || '')
      );
    }
    if (['2_1', '2_2', '2_3', '2_4'].includes(classification) || ['3'].includes(classification)) {
      return (
        typeof result[detailProgressInfo]?.value === 'undefined'
        || !['NN', 'N', 'A', 'AA'].includes(result[detailProgressInfo]?.value || '')
      );
    }
    return true;
  }, [classification, detailProgressInfo, progressInfo, result, firstClassificationResult]);

  const isPrevDisabled = useMemo(() => {
    if (classification === '1') {
      return progressInfo === 0;
    }
    if (['2_1', '2_2', '2_3', '2_4'].includes(classification) || ['3'].includes(classification)) {
      return detailProgressInfo === 0;
    }
    return true;
  }, [classification, detailProgressInfo, progressInfo]);

  const NNInfo = useMemo(() => {
    return inspectionDetail2Info.NNInfo;
  }, [inspectionDetail2Info.NNInfo]);
  const NInfo = useMemo(() => {
    return inspectionDetail2Info.NInfo;
  }, [inspectionDetail2Info.NInfo]);
  const AInfo = useMemo(() => {
    return inspectionDetail2Info.AInfo;
  }, [inspectionDetail2Info.AInfo]);
  const AAInfo = useMemo(() => {
    return inspectionDetail2Info.AAInfo;
  }, [inspectionDetail2Info.AAInfo]);

  const NNTempNNInfo = useMemo(() => {
    return inspectionDetail3Info2.NNTempNNInfo;
  }, [inspectionDetail3Info2.NNTempNNInfo]);
  const NNTempNInfo = useMemo(() => {
    return inspectionDetail3Info2.NNTempNInfo;
  }, [inspectionDetail3Info2.NNTempNInfo]);
  const NNTempAInfo = useMemo(() => {
    return inspectionDetail3Info2.NNTempAInfo;
  }, [inspectionDetail3Info2.NNTempAInfo]);
  const NNTempAAInfo = useMemo(() => {
    return inspectionDetail3Info2.NNTempAAInfo;
  }, [inspectionDetail3Info2.NNTempAAInfo]);
  const NTempNNInfo = useMemo(() => {
    return inspectionDetail3Info2.NTempNNInfo;
  }, [inspectionDetail3Info2.NTempNNInfo]);
  const NTempNInfo = useMemo(() => {
    return inspectionDetail3Info2.NTempNInfo;
  }, [inspectionDetail3Info2.NTempNInfo]);
  const NTempAInfo = useMemo(() => {
    return inspectionDetail3Info2.NTempAInfo;
  }, [inspectionDetail3Info2.NTempAInfo]);
  const NTempAAInfo = useMemo(() => {
    return inspectionDetail3Info2.NTempAAInfo;
  }, [inspectionDetail3Info2.NTempAAInfo]);

  //
  const ATempNNInfo = useMemo(() => {
    return inspectionDetail3Info2.ATempNNInfo;
  }, [inspectionDetail3Info2.ATempNNInfo]);
  const ATempNInfo = useMemo(() => {
    return inspectionDetail3Info2.ATempNInfo;
  }, [inspectionDetail3Info2.ATempNInfo]);
  const ATempAInfo = useMemo(() => {
    return inspectionDetail3Info2.ATempAInfo;
  }, [inspectionDetail3Info2.ATempAInfo]);
  const ATempAAInfo = useMemo(() => {
    return inspectionDetail3Info2.ATempAAInfo;
  }, [inspectionDetail3Info2.ATempAAInfo]);
  //
  const AATempNNInfo = useMemo(() => {
    return inspectionDetail3Info2.AATempNNInfo;
  }, [inspectionDetail3Info2.AATempNNInfo]);
  const AATempNInfo = useMemo(() => {
    return inspectionDetail3Info2.AATempNInfo;
  }, [inspectionDetail3Info2.AATempNInfo]);
  const AATempAInfo = useMemo(() => {
    return inspectionDetail3Info2.AATempAInfo;
  }, [inspectionDetail3Info2.AATempAInfo]);
  const AATempAAInfo = useMemo(() => {
    return inspectionDetail3Info2.AATempAAInfo;
  }, [inspectionDetail3Info2.AATempAAInfo]);

  return {
    progressInfo,
    buttonInfo,
    ipsatives,
    handlePrev,
    handleNext,
    isNextDisabled,
    isPrevDisabled,
    NNInfo,
    NInfo,
    AInfo,
    AAInfo,
    detailProgressInfo,
    NNTempNNInfo,
    NNTempNInfo,
    NNTempAInfo,
    NNTempAAInfo,
    NTempNNInfo,
    NTempNInfo,
    NTempAInfo,
    NTempAAInfo,
    ATempNNInfo,
    ATempNInfo,
    ATempAInfo,
    ATempAAInfo,
    AATempNNInfo,
    AATempNInfo,
    AATempAInfo,
    AATempAAInfo,
  };
};
